<template>
  <div class="zm-form-item" :class="[{
      'zm-form-item--feedback': ZmForm && ZmForm.statusIcon,
      'is-error': validateState === 'error',
      'is-validating': validateState === 'validating',
      'is-success': validateState === 'success',
      'is-required': isRequired || required,
      'is-no-asterisk': ZmForm && ZmForm.hideRequiredAsterisk
    },
    sizeClass ? 'zm-form-item--' + sizeClass : ''
  ]">
    <label :for="labelFor" class="zm-form-item__label" :style="labelStyle" v-if="label || $slots.label">
      <slot name="label">{{label + form.labelSuffix}}</slot>
    </label>
    <div class="zm-form-item__content" :style="contentStyle">
      <slot></slot>
      <transition name="zm-zoom-in-top">
        <slot
          v-if="isShowErrorMessage"
          name="error"
          :error="validateMessage">
          <div
            class="zm-form-item__error"
            role="alert"
            :aria-describedby="_formItemErrorId"
            :class="{
              'zm-form-item__error--inline': typeof inlineMessage === 'boolean'
                ? inlineMessage
                : (ZmForm && ZmForm.inlineMessage || false)
            }"
          >
            <span class="zm-sr-only" :id="_formItemErrorId">{{`${label+form.labelSuffix} ${validateMessage}` }}</span>
            {{validateMessage}}
          </div>
        </slot>
      </transition>
    </div>
  </div>
</template>
<script>
  import AsyncValidator from 'async-validator';
  import emitter from '../../mixins/emitter';
  import objectAssign from '../../utils/merge';
  import { noop, getPropByPath } from '../../utils/util';
  let _uid = 0;

  function uid () {
    return _uid++;
  }
  export default {
    name: 'ZmFormItem',

    componentName: 'ZmFormItem',

    mixins: [emitter],

    provide() {
      return {
        ZmFormItem: this
      };
    },

    inject: ['ZmForm'],

    props: {
      label: String,
      labelWidth: String,
      prop: String,
      required: {
        type: Boolean,
        default: undefined
      },
      rules: [Object, Array],
      error: String,
      validateStatus: String,
      for: String,
      inlineMessage: {
        type: [String, Boolean],
        default: ''
      },
      showMessage: {
        type: Boolean,
        default: true
      }
      // size: String
    },
    watch: {
      error: {
        immediate: true,
        handler(value) {
          this.validateMessage = value;
          this.validateState = value ? 'error' : '';
        }
      },
      validateStatus(value) {
        this.validateState = value;
      }
    },
    computed: {
      _formItemErrorId () {
        return this.id ? this.id : 'form-item-error-' + uid();
      },
      labelFor() {
        return this.for || this.prop;
      },
      labelStyle() {
        const ret = {};
        if (this.form.labelPosition === 'top') return ret;
        const labelWidth = this.labelWidth || this.form.labelWidth;
        if (labelWidth) {
          ret.width = labelWidth;
        }
        return ret;
      },
      contentStyle() {
        const ret = {};
        const label = this.label;
        if (this.form.labelPosition === 'top' || this.form.inline) return ret;
        if (!label && !this.labelWidth && this.isNested) return ret;
        const labelWidth = this.labelWidth || this.form.labelWidth;
        if (labelWidth) {
          ret.marginLeft = labelWidth;
        }
        return ret;
      },
      form() {
        let parent = this.$parent;
        let parentName = parent.$options.componentName;
        while (parentName !== 'ZmForm') {
          if (parentName === 'ZmFormItem') {
            this.isNested = true;
          }
          parent = parent.$parent;
          parentName = parent.$options.componentName;
        }
        return parent;
      },
      fieldValue() {
        const model = this.form.model;
        if (!model || !this.prop) { return; }

        let path = this.prop;
        if (path.indexOf(':') !== -1) {
          path = path.replace(/:/, '.');
        }

        return getPropByPath(model, path, true).v;
      },
      isRequired() {
        let rules = this.getRules();
        let isRequired = false;

        if (rules && rules.length) {
          rules.every(rule => {
            if (rule.required) {
              isRequired = true;
              return false;
            }
            return true;
          });
        }
        return isRequired;
      },
      _formSize() {
        return this.ZmForm.size;
      },
      elFormItemSize() {
        return this.size || this._formSize;
      },
      sizeClass() {
        return this.elFormItemSize || (this.$ELEMENT || {}).size;
      },
      isShowErrorMessage() {
        return this.validateState === 'error' && this.showMessage && this.form.showMessage
      }
    },
    data() {
      return {
        id:'',
        validateState: '',
        validateMessage: '',
        validateDisabled: false,
        validator: {},
        isNested: false
      };
    },
    methods: {
      validate(trigger, callback = noop) {
        this.validateDisabled = false;
        const rules = this.getFilteredRule(trigger);
        if ((!rules || rules.length === 0) && this.required === undefined) {
          callback();
          return true;
        }

        this.validateState = 'validating';

        const descriptor = {};
        if (rules && rules.length > 0) {
          rules.forEach(rule => {
            delete rule.trigger;
          });
        }
        descriptor[this.prop] = rules;

        const validator = new AsyncValidator(descriptor);
        const model = {};

        model[this.prop] = this.fieldValue;

        validator.validate(model, { firstFields: true }, (errors, invalidFields) => {
          this.validateState = !errors ? 'success' : 'error';
          this.validateMessage = errors ? errors[0].message : '';

          callback(this.validateMessage, invalidFields);
          this.ZmForm && this.ZmForm.$emit('validate', this.prop, !errors, this.validateMessage || null);
        });
      },
      clearValidate() {
        this.validateState = '';
        this.validateMessage = '';
        this.validateDisabled = false;
      },
      resetField() {
        this.validateState = '';
        this.validateMessage = '';

        let model = this.form.model;
        let value = this.fieldValue;
        let path = this.prop;
        if (path.indexOf(':') !== -1) {
          path = path.replace(/:/, '.');
        }

        let prop = getPropByPath(model, path, true);

        this.validateDisabled = true;
        if (Array.isArray(value)) {
          prop.o[prop.k] = [].concat(this.initialValue);
        } else {
          prop.o[prop.k] = this.initialValue;
        }

        this.broadcast('ZmTimeSelect', 'fieldReset', this.initialValue);
      },
      getRules() {
        let formRules = this.form.rules;
        const selfRules = this.rules;
        const requiredRule = this.required !== undefined ? { required: !!this.required } : [];

        const prop = getPropByPath(formRules, this.prop || '');
        formRules = formRules ? (prop.o[this.prop || ''] || prop.v) : [];

        return [].concat(selfRules || formRules || []).concat(requiredRule);
      },
      getFilteredRule(trigger) {
        const rules = this.getRules();

        return rules.filter(rule => {
          if (!rule.trigger || trigger === '') return true;
          if (Array.isArray(rule.trigger)) {
            return rule.trigger.indexOf(trigger) > -1;
          } else {
            return rule.trigger === trigger;
          }
        }).map(rule => objectAssign({}, rule));
      },
      onFieldBlur() {
        this.validate('blur');
      },
      onFieldChange() {
        if (this.validateDisabled) {
          this.validateDisabled = false;
          return;
        }

        this.validate('change');
      }
    },
    mounted() {
      if (this.prop) {
        this.dispatch('ZmForm', 'el.form.addField', [this]);

        let initialValue = this.fieldValue;
        if (Array.isArray(initialValue)) {
          initialValue = [].concat(initialValue);
        }
        Object.defineProperty(this, 'initialValue', {
          value: initialValue
        });

        let rules = this.getRules();

        if (rules.length || this.required !== undefined) {
          this.$on('el.form.blur', this.onFieldBlur);
          this.$on('el.form.change', this.onFieldChange);
        }
      }
    },
    beforeDestroy() {
      this.dispatch('ZmForm', 'el.form.removeField', [this]);
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/mixins/utils";
  @import "../../scss/common/var";

  @include b(form-item) {
    margin-bottom: 22px;
    @include utils-clearfix;

    & .zm-form-item {
      margin-bottom: 0;
    }

    & .zm-input__validateIcon {
      display: none;
    }

    /*@include m(medium) {
      .zm-form-item__label {
        line-height: 36px;
      }
      .zm-form-item__content {
        line-height: 36px;
      }
    }
    @include m(small) {
      .zm-form-item__label {
        line-height: 32px;
      }
      &.zm-form-item {
        margin-bottom: 18px;
      }
      .zm-form-item__error {
        padding-top: 2px;
      }
    }
    @include m(mini) {
      .zm-form-item__label {
        line-height: 24px;
      }
      .zm-form-item__content {
        line-height: 24px;
      }
      &.zm-form-item {
        margin-bottom: 18px;
      }
      .zm-form-item__error {
        padding-top: 1px;
      }
    }*/

    @include e(label) {
      text-align: right;
      vertical-align: middle;
      float: left;
      font-size: inherit;
      color: $--color-text-secondary;
      line-height: 32px;
      padding: 0 12px 0 0;
      box-sizing: border-box;
      margin-bottom: 0;
    }
    @include e(content) {
      position: relative;
      font-size: inherit;
      @include utils-clearfix;

      .zm-input-group {
        vertical-align: middle;
      }
    }
    @include e(error) {
      color: $--color-danger;
      font-size: 14px;
      line-height: 1;
      padding-top: 4px;
      position: absolute;
      top: 100%;
      left: 0;

      @include m(inline) {
        position: relative;
        top: auto;
        left: auto;
        display: inline-block;
      }
    }

    @include when(required) {
      @include pseudo('not(.is-no-asterisk)') {
        & > .zm-form-item__label:before {
          content: '*';
          color: $--color-danger;
          margin-right: 4px;
        }
      }
    }

    @include when(error) {
      & .zm-input__inner,
      & .zm-textarea__inner {
        &, &:focus {
          border-color: $--color-danger;
        }
      }
      & .zm-input-group__append,
      & .zm-input-group__prepend {
        & .zm-input__inner {
          border-color: transparent;
        }
      }
      .zm-input__validateIcon {
        color: $--color-danger;
      }
      .zm-select .zm-select-input{
        border-color: $--color-danger;
      }
      .zm-select-fix{
        border: 1px solid $--color-danger;
      }
    }
    @include when(success) {
      & .zm-input-group__append,
      & .zm-input-group__prepend {
        & .zm-input__inner {
          border-color: transparent;
        }
      }
      .zm-input__validateIcon {
        color: $--color-success;
      }
    }
    @include m(feedback) {
      .zm-input__validateIcon {
        display: inline-block;
      }
    }
  }
</style>
